import { useState, useEffect } from "react";
import { Calendar, Plus, Edit2, Trash2, Loader2 } from "lucide-react";
import { IEventResponse } from "@/types/EventTypes";
import { V1BEClient } from "@/clients";
import { useAuthStore } from "@/store/authStore";
import PageHeader from "@/components/ui/PageHeader";
import { toast } from "sonner";
import EventUserDialog from "@/components/protected/event-user/EventUserDialog";

export default function EventManagement() {
  const { setHasEvents } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState<IEventResponse[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<IEventResponse | null>(null);
  const [deletingEventId, setDeletingEventId] = useState<number | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<IEventResponse | null>(
    null
  );

  const eventTypes = [
    { value: "wedding", label: "Wedding" },
    { value: "debut", label: "Debut" },
    { value: "birthday", label: "Birthday" },
    { value: "corporate event", label: "Corporate Events" },
    { value: "others", label: "Others" },
  ];

  // Fetch events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const result = await V1BEClient.fetchMyEvents();
      if (result && result.data) {
        setEvents(result.data);
        setHasEvents(result.data.length > 0);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Failed to load events");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (event?: IEventResponse) => {
    if (event) {
      setEditingEvent(event);
    } else {
      setEditingEvent(null);
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingEvent(null);
  };

  const handleDeleteClick = (event: IEventResponse) => {
    setEventToDelete(event);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;

    setDeletingEventId(eventToDelete.id);
    try {
      const success = await V1BEClient.deleteEvent(eventToDelete.id);
      if (success) {
        toast.success("Event deleted successfully!");
        await fetchEvents();
        setIsDeleteDialogOpen(false);
        setEventToDelete(null);
      } else {
        toast.error("Failed to delete event");
      }
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Failed to delete event");
    } finally {
      setDeletingEventId(null);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setEventToDelete(null);
  };

  const getEventTypeLabel = (event: IEventResponse) => {
    const eventType = event.event_string_properties?.find(
      (p) => p.name === "event_type"
    )?.value;
    if (eventType === "others") {
      return (
        event.event_string_properties?.find(
          (p) => p.name === "event_type_other"
        )?.value || "Others"
      );
    }
    return eventTypes.find((t) => t.value === eventType)?.label || eventType;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="container">
      <div className="md:my-10 my-4 md:mx-0 mx-2">
        <PageHeader
          title="My Events"
          subtitle="Create and manage your events"
        />

        {/* Create Event Button */}
        <div className="mb-6 flex justify-end">
          <button
            onClick={() => handleOpenDialog()}
            className="flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-700 transition-colors shadow-sm hover:shadow-md"
          >
            <Plus className="w-5 h-5" />
            Create New Event
          </button>
        </div>

        {/* Events List */}
        {events.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No Events Yet
            </h3>
            <p className="text-gray-600 mb-6">
              Get started by creating your first event!
            </p>
            <button
              onClick={() => handleOpenDialog()}
              className="inline-flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create Your First Event
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <div
                key={event.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {event.title}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {getEventTypeLabel(event)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenDialog(event)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit event"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(event)}
                      disabled={deletingEventId === event.id}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete event"
                    >
                      {deletingEventId === event.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  {new Date(event.event_date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Event Dialog */}
        <EventUserDialog
          open={isDialogOpen}
          onOpenChange={handleCloseDialog}
          eventToEdit={editingEvent}
          onSuccess={fetchEvents}
        />

        {/* Delete Confirmation Dialog */}
        {isDeleteDialogOpen && eventToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Delete Event
                  </h3>
                  <p className="text-sm text-gray-600">
                    Are you sure you want to delete "{eventToDelete.title}"?
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleDeleteCancel}
                  disabled={deletingEventId !== null}
                  className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deletingEventId !== null}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {deletingEventId !== null ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Event
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
